import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled, { createGlobalStyle, css } from 'styled-components';

const HeaderGlobalStyles = createGlobalStyle`
  :root {
    --linksContainerHeight: initial;
    --mainContainerHeight: 70px;
    --searchBarHeight: 56px;
    --headerHeight: var(--mainContainerHeight);
    --headerPadding: 16px;

    @media ${from(Device.DesktopSmall)} {
      --linksContainerHeight: 32px;
      --mainContainerHeight: 72px;
      --headerHeight: calc(var(--linksContainerHeight) + var(--mainContainerHeight));
    }
  }
`;

export const customNavScrollbarStyles = css`
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${brand.grey[97]};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${brand.grey[80]};
    border-radius: 6px;
  }
`;

const Wrapper = styled.header`
  --searchWidth: ${fluid(340, 470, Device.DesktopSmall)};

  --logoWidth: 232px;

  position: relative;
  z-index: 100;
  height: calc(var(--headerHeight) + var(--searchBarHeight));
  color: ${brand.black};

  @media ${from(Device.DesktopSmall)} {
    --logoWidth: 273px;

    height: var(--headerHeight);
  }

  &::before {
    content: '';
    position: fixed;
    z-index: -1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100vh;
    background-color: ${rgba(brand.black, 0.6)};
    opacity: 0;
    pointer-events: none;
  }

  &[data-show-overlay='true']::before {
    opacity: 1;
  }

  &[data-dropdown-open='true'] {
    z-index: 1001;
  }
`;

const LinksContainer = styled.ul`
  display: none;

  @media ${from(Device.DesktopSmall)} {
    display: flex;
    place-items: center;
    place-content: flex-end;
    gap: 0px 8px;
    height: var(--linksContainerHeight);
    padding: 0px var(--headerPadding);
    background-color: ${brand.red.dark};

    // override legacy styles
    margin: 0;
  }
`;

const LinkItem = styled.li``;

const Link = styled.a`
  display: block;
  ${fonts.helvetica['73-bold-extended']}
  font-size: 11px;
  padding: 0px 12px;
  color: ${brand.white};
  text-transform: uppercase;
`;

const MainContainer = styled.div`
  position: relative;
  display: flex;
  place-items: center;
  place-content: space-between;
  gap: 20px;
  height: var(--mainContainerHeight);
  padding: 0px var(--headerPadding);
  background-color: ${brand.red.base};
`;

const Logo = styled.a`
  width: var(--logoWidth);
  height: 40px;
  flex-shrink: 0;

  @media ${from(Device.DesktopSmall)} {
    height: 46px;
  }
`;

const StoreLocatorLink = styled.a`
  display: inline-flex;
  place-content: center;
  place-items: center;
  color: ${brand.white};
  margin-left: auto;

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const SearchButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  place-content: center;
  place-items: center;
  width: 32px;
  height: 32px;
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin-left: auto;
  color: ${brand.white};

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MenuButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  place-content: center;
  place-items: center;
  width: 32px;
  height: 32px;
  appearance: none;
  background: none;
  border: none;
  padding: 0;

  svg {
    display: none;
  }

  &[aria-current='false'] svg:nth-child(1) {
    display: block;
  }

  &[aria-current='true'] svg:nth-child(2) {
    display: block;
    width: 20px;
    height: 20px;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }

  @media ${until(Device.DesktopSmall)} {
    [data-search-open='true'] & {
      opacity: 0;
      pointer-events: none;
    }
  }
`;

const SiteNavigation = styled.div`
  display: none;

  @media ${from(Device.DesktopSmall)} {
    display: flex;
    place-content: center;
    flex: 1 0 auto;
    height: var(--mainContainerHeight);
    padding-right: var(--logoWidth);
  }
`;

const Search = styled(m.div)`
  @media ${from(Device.DesktopSmall)} {
    display: flex;
    place-items: center;
    position: absolute;
    top: 0;
    right: var(--headerPadding);
    width: 100%;
    max-width: var(--searchWidth);
    height: 100%;

    &[data-device='mobile'] {
      display: none;
    }
  }

  @media ${until(Device.DesktopSmall)} {
    &[data-device='mobile'] {
      // Override the animation from framer-motion
      transform: translateY(0) !important;
      max-width: 100% !important;
      transition: none !important;
      background: ${brand.black};
      padding: calc(var(--headerPadding) / 2) var(--headerPadding);
      opacity: 1;
      height: var(--searchBarHeight);
    }

    &[data-device='desktop'] {
      // Override the animation from framer-motion
      transform: translateY(-50%) !important;
      max-width: calc(100vw - (var(--headerPadding) * 5)) !important;
      transition: opacity 0.3s linear !important;

      position: absolute;
      top: 50%;
      left: var(--headerPadding);
      width: 100%;
      opacity: 0;
      pointer-events: none;

      [data-search-open='true'] & {
        opacity: 1;
        pointer-events: auto;
      }
    }

    &[data-mobile-hidden='true'] {
      display: none;
    }
  }
`;

const CloseSearchButton = styled(m.button).attrs({ type: 'button' })`
  display: inline-flex;
  flex-direction: column;
  place-content: center;
  place-items: center;
  gap: 5px;
  ${fonts.helvetica['53-extended']}
  font-size: 14px;
  color: ${brand.white};
  padding: 0 12px;
  width: auto;
  height: 100%;
  appearance: none;
  background: none;
  border: none;
  outline: none;

  @media ${until(Device.DesktopSmall)} {
    position: absolute;
    top: 0;
    right: 0;
    width: 60px;
    height: 100%;
    font-size: 12px;
    background-color: ${brand.red.base};
  }

  svg {
    width: 24px;
    height: 24px;

    @media ${until(Device.DesktopSmall)} {
      width: 20px;
      height: 20px;
    }
  }
`;

export const HeaderStyles = {
  Wrapper,
  LinksContainer,
  LinkItem,
  Link,
  MainContainer,
  Logo,
  StoreLocatorLink,
  SearchButton,
  MenuButton,
  SiteNavigation,
  Search,
  CloseSearchButton,
  HeaderGlobalStyles,
};

export default HeaderStyles;
